import { CommonModule } from "@angular/common";
import { Component, computed, HostBinding, inject, OnInit, Signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { RouterModule } from "@angular/router";
import { TranslateModule } from "@ngx-translate/core";
import { injectQuery } from "@tanstack/angular-query-experimental";
import { MenuItem } from "primeng/api";
import { shareReplay } from "rxjs/operators";

import { AuthModule } from "@intocare/auth";
import { FeatureService, PermissionsService } from "@intocare/auth-data";
import { PageFilterQueryParam, PageFilterService, PageFilterType } from "@intocare/client-side-state";
import { PortalConfigurationService } from "@intocare/data-access/portal/organisation";
import { SharedCommunicationDataService } from "@intocare/data-access/shared/communication";
import { EnvironmentService } from "@intocare/environments";
import { OrganisationModel } from "@intocare/organisation/data";
import { ResidentsModel } from "@intocare/residents/data";
import { ContactComponent, NavService, UIModule } from "@intocare/ui";
import { ActivityBasedPollService, CONTEXT_TOKEN } from "@intocare/util";

import { PageFilterComponent } from "../../components/page-filter/page-filter.component";

/**
 * Inject the menu items, activating the notification counts as needed.
 *
 * This way of working will prevent unnecessary injections/subscriptions, as we only trigger the injection function for
 * menu items that will be active, after filtering them out based on the permissions and feature flags. These check only
 * happen once, because content must be loaded.
 *
 * TODO: This could be further optimized and turned into some utility to easily create menu items. Some menu items might
 * stand on their own functional-wise. These can thus be defined/configured in their own file/module/thing, and then
 * easily injected where needed, triggering subscriptions. In a way, each such menu item is its own
 * view-model/data-store, and be done with an injectable. This is exactly why Angular has multi-providers, so you could
 * then have every module that gets loaded provide their menu items, which then get injected into the menu based on
 * their config. Not that we have to do this, as explicit config has its own benefits, but food for thought. Another way
 * we can go about this is through components, which can then be placed into the template. If a component gets rendered
 * depends on the component itself, and for things as `hasNotifications`, a `viewChildren` query can be used for the
 * calculation.
 */
function injectSideMenu(
	items: (Omit<MenuItem, "notifications"> & { injectNotificationCount?: () => Signal<number> })[],
) {
	const permissionsService = inject(PermissionsService);
	const featureService = inject(FeatureService);

	return items
		.filter(
			(item) =>
				permissionsService.userHasPermissions(item.permissions || [])
				&& featureService.hasFeatures(item.features || []),
		)
		.map(({ injectNotificationCount, ...item }) => ({
			...item,
			...(injectNotificationCount && { notificationCount: injectNotificationCount() }),
			...(item.items ? { items: injectSideMenu(item.items) } : undefined),
		}));
}

@Component({
	imports: [AuthModule, CommonModule, PageFilterComponent, RouterModule, TranslateModule, UIModule, ContactComponent],
	templateUrl: "home.component.html",
	styleUrls: ["home.component.scss"],
})
export class HomeComponent implements OnInit {
	private readonly navService = inject(NavService);
	private readonly organisationModel = inject(OrganisationModel);
	private readonly configurationService = inject(PortalConfigurationService);
	private readonly residentsModel = inject(ResidentsModel);
	protected readonly environmentService = inject(EnvironmentService);
	private readonly pageFilterService = inject(PageFilterService);
	private readonly contextService = inject(CONTEXT_TOKEN);
	private readonly activityBasedPollService = inject(ActivityBasedPollService);
	private readonly sharedCommunicationDataService = inject(SharedCommunicationDataService);

	private readonly _messagesPoll = this.activityBasedPollService.register(() =>
		this.sharedCommunicationDataService.invalidateMessagesState(),
	);

	@HostBinding("attr.tabindex") tabindex = 0;

	protected supportInfo$ = this.organisationModel.supportInfo$;
	protected readonly homes = computed(() =>
		this.contextService
			.context()
			.accessibleSites.map((site) => ({ id: `${this.contextService.context().hq}.${site.home}`, name: site.name })),
	);
	protected readonly departmentsQuery = injectQuery(() =>
		this.configurationService.getDepartmentsWithAtLeastOneRoomQueryOptions(
			this.contextService.context().currentSite.id,
		),
	);

	protected readonly residents$ = this.residentsModel.finalizedResidents$.pipe(
		shareReplay({ bufferSize: 1, refCount: false }),
	);
	private readonly residents = toSignal(this.residents$);
	private readonly pageFilterResidentValue = this.pageFilterService.getValue(PageFilterQueryParam.NAME_RESIDENT);

	protected filterConfigSignal = this.pageFilterService.getFilterConfigSignal();

	protected readonly isResidentSelectionRequired = computed(
		() =>
			!!this.residents()?.length
			&& !this.departmentsQuery.isLoading()
			&& this.filterConfigSignal().residentFilter === PageFilterType.REQUIRED
			&& !this.pageFilterResidentValue(),
	);
	protected readonly supportInfo = computed(() => this.contextService.context().supportInfo);

	private readonly basePathOrganisation = "organisation/";
	private readonly basePathResidents = "residents/";
	private readonly basePathCommunication = "communication/";

	protected readonly sideMenu = injectSideMenu([
		{
			label: "general.titles.dashboard",
			icon: "fa fa-bar-chart",
			routerLink: "dashboard",
			automationId: "dashboard",
		},
		{
			label: "general.titles.communication",
			icon: "fa fa-envelope",
			automationId: "tab-communication",
			permissions: ["PORTAL.INTERNAL_COMMUNICATION"],
			injectNotificationCount: () => {
				const sharedCommunicationDataService = inject(SharedCommunicationDataService);
				const messagesStateQuery = injectQuery(() => sharedCommunicationDataService.messagesStateQueryOptions());
				return computed(() => messagesStateQuery.data()?.data?.messages?.unread?.count ?? 0);
			},
			items: [
				{
					label: "general.titles.receivedMessages",
					routerLink: [this.basePathCommunication + "internal-communication/received"],
					automationId: "tab-received-messages",
					injectNotificationCount: () => {
						const sharedCommunicationDataService = inject(SharedCommunicationDataService);
						const messagesStateQuery = injectQuery(() => sharedCommunicationDataService.messagesStateQueryOptions());
						return computed(() => messagesStateQuery.data()?.data?.messages?.unread?.count ?? 0);
					},
				},
				{
					label: "general.titles.sentMessages",
					routerLink: [this.basePathCommunication + "internal-communication/sent"],
					automationId: "tab-sent-messages",
				},
			],
		},
		{
			label: "general.titles.new_history",
			icon: "fa fa-history",
			routerLink: this.basePathOrganisation + "history2",
			automationId: "tab-history",
			permissions: ["PORTAL.ORGANIZATION_HISTORY"],
		},
		{
			label: "general.titles.mdc",
			icon: "fa fa-book",
			routerLink: [this.basePathOrganisation + "mdc"],
			automationId: "tab-multidisciplinary-consultation-1",
			permissions: ["PORTAL.MDO"],
		},
		...(this.environmentService.isDev()
			? [
					{
						label: "general.titles.mdc2",
						icon: "fa fa-book",
						routerLink: [this.basePathOrganisation + "mdc2"],
						automationId: "tab-multidisciplinary-consultation-2",
						permissions: ["PORTAL.MDO"],
					},
				]
			: []),
		{
			label: "general.titles.residentAdministration",
			icon: "fa fa-users",
			automationId: "tab-residents",
			permissions: {
				$or: [
					"PORTAL.CREATE_RESIDENT",
					"PORTAL.PERSONAL_INFORMATION",
					"PORTAL.SOCIAL_INFORMATION",
					"PORTAL.MEDICAL_INFORMATION",
					"PORTAL.ADMISSION_INFORMATION",
					"PORTAL.EARLY_CARE",
					"PORTAL.HISTORY_AND_GOALS",
					"PORTAL.CONTACTS_INFORMATION",
					"PORTAL.SHT",
					"PORTAL.ORGANIZATION_OVERVIEW",
					"PORTAL.ORGANISATION_STRUCTURE",
				],
			},
			items: [
				{
					label: "general.titles.onboard",
					routerLink: this.basePathResidents + "management/onboarding",
					automationId: "tab-resident-onboard",
					permissions: ["PORTAL.CREATE_RESIDENT"],
					features: ["OnboardResidents"],
				},
				{
					label: "general.titles.residentInfo",
					routerLink: this.basePathResidents + "edit",
					queryParamsHandling: "preserve",
					automationId: "tab-resident-information",
					permissions: {
						$or: [
							"PORTAL.PERSONAL_INFORMATION",
							"PORTAL.SOCIAL_INFORMATION",
							"PORTAL.MEDICAL_INFORMATION",
							"PORTAL.ADMISSION_INFORMATION",
							"PORTAL.EARLY_CARE",
							"PORTAL.HISTORY_AND_GOALS",
							"PORTAL.CONTACTS_INFORMATION",
						],
					},
				},
				{
					label: "general.titles.statusHistory",
					routerLink: this.basePathResidents + "status",
					queryParamsHandling: "preserve",
					automationId: "resident-status-nav",
					permissions: ["PORTAL.SHT"],
				},
				{
					label: "general.titles.residents-overview",
					routerLink: this.basePathOrganisation + "residents",
					automationId: "tab-residents-overview",
					permissions: ["PORTAL.ORGANIZATION_OVERVIEW"],
				},
				{
					label: "general.titles.organisationStructure",
					routerLink: [this.basePathOrganisation + "occupation"],
					automationId: "tab-organisation-structure",
					permissions: ["PORTAL.ORGANISATION_STRUCTURE"],
				},
			],
		},
		{
			label: "general.titles.care",
			icon: "fa fa-medkit",
			automationId: "tab-care",
			permissions: {
				$or: [
					"PORTAL.RESIDENT_OVERVIEW",
					"PORTAL.CARE",
					"PORTAL.BELRAI",
					"PORTAL.CARE_REGISTRATIONS",
					"PORTAL.WOUNDCARE",
					"PORTAL.OBSERVATIONS",
					"PORTAL.FOLLOW_UP_PLANS",
					"PORTAL.ASSESSMENTS",
				],
			},
			items: [
				{
					label: "general.titles.overview",
					routerLink: this.basePathResidents,
					queryParamsHandling: "preserve",
					routerLinkActiveOptions: { exact: "true" },
					automationId: "tab-overview",
					permissions: ["PORTAL.RESIDENT_OVERVIEW"],
				},
				{
					label: "general.titles.detail",
					routerLink: this.basePathResidents + "detail",
					queryParamsHandling: "preserve",
					automationId: "tab-detail",
					permissions: ["PORTAL.CARE"],
				},
				{
					label: "general.titles.caretree",
					routerLink: [this.basePathResidents + "planning", "caretree"],
					queryParamsHandling: "preserve",
					automationId: "tab-caretree",
					permissions: ["PORTAL.CARE"],
				},
				{
					label: "general.titles.planner",
					routerLink: [this.basePathResidents + "planning", "planner"],
					queryParamsHandling: "preserve",
					automationId: "tab-careplan",
					permissions: ["PORTAL.CARE"],
				},
				{
					label: "general.titles.wound-care",
					routerLink: this.basePathResidents + "wound-care",
					queryParamsHandling: "preserve",
					automationId: "tab-wound-care",
					permissions: ["PORTAL.WOUNDCARE"],
				},
				{
					label: "BelRAI",
					routerLink: [this.basePathResidents + "belrai", "assessments"],
					queryParamsHandling: "preserve",
					automationId: "belrai-nav",
					features: ["BelRAI"],
					permissions: ["PORTAL.BELRAI"],
				},
				{
					label: "general.titles.careRegistrations",
					routerLink: this.basePathResidents + "care-registrations",
					queryParamsHandling: "preserve",
					automationId: "tab-care-registrations",
					permissions: ["PORTAL.CARE_REGISTRATIONS"],
				},
				{
					label: "general.titles.observations",
					routerLink: this.basePathResidents + "observations",
					queryParamsHandling: "preserve",
					automationId: "tab-observations",
					permissions: ["PORTAL.OBSERVATIONS"],
				},
				{
					label: "general.titles.followUpPlans",
					routerLink: this.basePathResidents + "follow-up-plans",
					queryParamsHandling: "preserve",
					automationId: "tab-follow-up-plans",
					permissions: ["PORTAL.FOLLOW_UP_PLANS"],
					isNewFunctionality: true,
				},
				{
					label: "general.titles.assessments",
					routerLink: this.basePathResidents + "assessments",
					queryParamsHandling: "preserve",
					automationId: "tab-assessments",
					permissions: ["PORTAL.ASSESSMENTS"],
					isNewFunctionality: true,
				},
			],
		},
		{
			label: "labels.medication-overview.properties.medication",
			icon: "fa fae-medicine",
			automationId: "tab-medication",
			features: ["Medication"],
			permissions: { $or: ["PORTAL.MEDICATION", "PORTAL.MEDICATION_PLANNING"] },
			items: [
				{
					label: "general.titles.medication_planning",
					routerLink: this.basePathResidents + "medication",
					queryParamsHandling: "preserve",
					automationId: "tab-medication-planning",
					features: ["Medication"],
					permissions: ["PORTAL.MEDICATION_PLANNING"],
				},
				{
					label: "general.titles.medication-preparation",
					routerLink: [this.basePathOrganisation + "medication", "tasks", "prepare"],
					automationId: "tab-prepare",
					permissions: ["PORTAL.MEDICATION"],
				},
				{
					label: "general.titles.medication-verification",
					routerLink: [this.basePathOrganisation + "medication", "tasks", "verify"],
					automationId: "tab-verify",
					permissions: ["PORTAL.MEDICATION"],
				},
				{
					label: "labels.medication-overview.orders",
					routerLink: [this.basePathOrganisation + "medication", "orders"],
					automationId: "tab-orders",
					permissions: ["PORTAL.MEDICATION"],
				},
				{
					label: "labels.medication-overview.future-orders",
					routerLink: [this.basePathOrganisation + "medication", "future-orders"],
					automationId: "tab-future-orders",
					permissions: ["PORTAL.MEDICATION"],
				},
				{
					label: "labels.medication-overview.prescriptions",
					routerLink: [this.basePathOrganisation + "medication", "prescriptions"],
					automationId: "tab-prescriptions",
					permissions: ["PORTAL.MEDICATION"],
				},
			],
		},
		{
			label: "general.titles.agenda",
			icon: "fa fa-calendar",
			automationId: "tab-calendar",
			permissions: ["PORTAL.CALENDAR"],
			items: [
				{
					label: "general.titles.appointments",
					routerLink: [this.basePathResidents + "agenda"],
					queryParamsHandling: "preserve",
					automationId: "tab-appointments",
				},
				{
					label: "general.titles.activities",
					routerLink: [this.basePathOrganisation + "agenda"],
					automationId: "tab-activities",
				},
				{
					label: "general.titles.activity_evaluations",
					routerLink: [this.basePathOrganisation + "evaluations"],
					automationId: "tab-evaluations",
				},
			],
		},
		{
			label: "general.titles.reports",
			icon: "fa fa-file-text-o",
			automationId: "tab-documents",
			permissions: {
				$or: [
					"PORTAL.QUALITY_INDICATOR",
					"PORTAL.ALARMS",
					"PORTAL.ORGANIZATION_DOCUMENTS",
					"PORTAL.RESIDENT_DOCUMENTS",
					"PORTAL.OBSERVATION_REPORTS",
				],
			},
			items: [
				{
					label: "general.titles.qaIndicators",
					routerLink: [this.basePathOrganisation + "qaindicators2"],
					automationId: "tab-quality-indicators",
					permissions: ["PORTAL.QUALITY_INDICATOR"],
				},
				{
					label: "general.titles.alarms",
					routerLink: [this.basePathOrganisation + "alarms"],
					automationId: "tab-alarms",
					features: ["CallAlarms"],
					permissions: ["PORTAL.ALARMS"],
				},
				{
					label: "general.titles.inspection",
					routerLink: [this.basePathOrganisation + "documents", "inspection"],
					automationId: "tab-inspection",
					permissions: ["PORTAL.ORGANIZATION_DOCUMENTS"],
				},
				{
					label: "general.titles.care-organisation",
					routerLink: [this.basePathOrganisation + "documents", "care"],
					automationId: "tab-reports-organisation",
					permissions: ["PORTAL.ORGANIZATION_DOCUMENTS"],
				},
				{
					label: "general.titles.care-residents",
					routerLink: [this.basePathResidents + "documents", "reports"],
					queryParamsHandling: "preserve",
					automationId: "tab-reports-residents",
					permissions: ["PORTAL.RESIDENT_DOCUMENTS"],
				},
				{
					label: "general.titles.uploads",
					routerLink: [this.basePathResidents + "documents", "uploads"],
					queryParamsHandling: "preserve",
					automationId: "tab-uploads",
					permissions: ["PORTAL.RESIDENT_DOCUMENTS"],
				},
				{
					label: "general.titles.templates",
					routerLink: [this.basePathOrganisation + "documents", "templates"],
					automationId: "tab-templates",
					permissions: ["PORTAL.ORGANIZATION_DOCUMENTS"],
				},
				{
					label: "general.titles.observations",
					routerLink: [this.basePathOrganisation + "observations"],
					automationId: "tab-organisation-observations",
					permissions: ["PORTAL.OBSERVATION_REPORTS"],
				},
			],
		},
		{
			label: "general.titles.settings",
			icon: "fa fa-sliders",
			automationId: "tab-settings",
			permissions: {
				$or: [
					"PORTAL.EXTERNAL_EMPLOYEES",
					"PORTAL.INTERNAL_EMPLOYEES",
					"PORTAL.MANAGE_CALL_ALARMS",
					"PORTAL.CARE_FACILITIES",
					"PORTAL.CONFIGURATION",
				],
			},
			items: [
				{
					label: "general.titles.external_staff",
					routerLink: [this.basePathOrganisation + "staff", "external"],
					automationId: "tab-external-staff",
					permissions: ["PORTAL.EXTERNAL_EMPLOYEES"],
				},
				{
					label: "general.titles.internal_staff",
					routerLink: [this.basePathOrganisation + "staff", "internal"],
					automationId: "tab-internal-staff",
					permissions: ["PORTAL.INTERNAL_EMPLOYEES"],
				},
				{
					label: "general.titles.alarmLinks",
					routerLink: [this.basePathOrganisation + "manage-alarms"],
					automationId: "tab-alarm-links",
					permissions: ["PORTAL.MANAGE_CALL_ALARMS"],
				},
				{
					label: "general.titles.siteDetails",
					automationId: "tab-site-config",
					routerLink: [this.basePathOrganisation + "configuration"],
					routerLinkActiveOptions: { exact: "true" },
					permissions: ["PORTAL.CONFIGURATION"],
				},
				{
					label: "general.titles.careFacilities",
					automationId: "tab-care-facilities",
					routerLink: [this.basePathOrganisation + "configuration", "care-facilities"],
					permissions: ["PORTAL.CARE_FACILITIES"],
				},
			],
		},
	]);

	ngOnInit() {
		this.navService.setMenuItems(this.sideMenu);
	}
}
